// src/pages/account/PrivacyPage.jsx
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "../../utils/apparifySwal";

import { useAuth } from "../../context/AuthContext";
import { useAccountSwal } from "../../features/account/useAccountSwal";
import SectionCard from "../../components/account/SectionCard";
import { userAPI } from "../../services/profileService";

export default function PrivacyPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const { swalBase } = useAccountSwal();

  const onAuthExpired = useCallback(() => {
    logout();
    navigate("/login", { replace: true });
  }, [logout, navigate]);

  const deleteAccount = async () => {
    const r = await Swal.fire({
      ...swalBase,
      icon: "warning",
      title: "DELETE ACCOUNT?",
      html: "This action is permanent.<br/><br/>Type <b>DELETE</b> to confirm.",
      input: "text",
      inputPlaceholder: "Type DELETE",
      showCancelButton: true,
      confirmButtonText: "DELETE",
      cancelButtonText: "CANCEL",
      preConfirm: (value) => {
        if (String(value || "").trim().toUpperCase() !== "DELETE") {
          Swal.showValidationMessage("Please type DELETE exactly.");
          return false;
        }
        return true;
      },
    });

    if (!r.isConfirmed) return;

    Swal.fire({
      ...swalBase,
      title: "DELETING…",
      html: "Removing your account",
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      didOpen: () => Swal.showLoading(),
    });

    try {
      // optional chaining in case endpoint isn't ready yet
      await userAPI.deleteMe?.();

      Swal.close();

      await Swal.fire({
        ...swalBase,
        icon: "success",
        title: "ACCOUNT DELETED",
        html: "Your account was deleted successfully.",
        confirmButtonText: "OK",
      });

      logout();
      navigate("/login", { replace: true });
    } catch (e) {
      Swal.close();

      if (e?.status === 401) {
        onAuthExpired();
        return;
      }

      await Swal.fire({
        ...swalBase,
        icon: "error",
        title: "DELETE FAILED",
        html:
          e?.message ||
          "Account deletion failed. Please try again later.",
        confirmButtonText: "OK",
      });
    }
  };

  return (
    <div className="space-y-6">
      <SectionCard
        title="Privacy & Security"
        subtitle="Manage privacy-related actions for your account."
      >
        <div className="space-y-4">
          <div className="rounded-xl border border-neutral-200 bg-neutral-50 p-3 dark:border-neutral-800 dark:bg-neutral-900/60">
            <p className="text-sm text-neutral-700 dark:text-neutral-300">
              Keep your account secure. If you no longer want to use this account,
              you may delete it permanently.
            </p>
            <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
              This action cannot be undone.
            </p>
          </div>

          <button
            type="button"
            onClick={deleteAccount}
            className="
              w-full rounded-xl border px-4 py-2 text-sm font-semibold transition
              border-red-500/60 text-red-700 hover:bg-red-50
              dark:border-red-500/40 dark:text-red-300 dark:hover:bg-red-500/10
              focus:outline-none focus:ring-2 focus:ring-red-500/30
            "
          >
            Delete Account
          </button>
        </div>
      </SectionCard>
    </div>
  );
}
